<template>
    <v-row dense>
        <v-col cols="12" md="8">
            <!-- Description -->
            <div class="mb-4 pa-4" style="background-color: #f5f5f5; border-radius: 4px">
                <p class="text-body-1 mb-0">
                    {{ $t('sustainable_room_amenities.description') }}
                </p>
            </div>

            <!-- Amenities Selection -->
            <div class="mb-4 pa-4" style="background-color: var(--zsh-primary); color: white; border-radius: 4px">
                <div class="d-flex align-center">
                    <v-icon class="mr-2" color="white">mdi-information-outline</v-icon>
                    <p class="text-body-1 mb-0">
                        {{ $t('sustainable_room_amenities.instruction') }}
                    </p>
                    <v-icon class="ml-2" color="white">mdi-information-outline</v-icon>
                </div>
            </div>

            <!-- Loading State -->
            <template v-if="sustainableAmenitiesLoading">
                <v-row dense>
                    <v-col cols="12" class="text-center">
                        <v-progress-circular indeterminate color="primary" />
                        <p class="mt-2">{{ $t('sustainable_room_amenities.loading_amenities') }}</p>
                    </v-col>
                </v-row>
            </template>

            <!-- Amenities Categories -->
            <template v-else-if="sustainableAmenityCategories.length > 0">
                <v-row dense>
                    <v-col v-for="category in sustainableAmenityCategories" :key="category" cols="12" class="mb-4">
                        <!-- Category Header with Column Names -->
                        <div
                            class="pa-3 mb-0"
                            style="background-color: var(--zsh-primary-dark); color: white; border-radius: 4px 4px 0 0"
                        >
                            <v-row dense align="center" no-gutters>
                                <!-- For OTHER category, make amenity name column wider -->
                                <v-col :cols="category === 'OTHER' ? 12 : 12" :md="category === 'OTHER' ? 8 : 4">
                                    <h4 class="text-subtitle-1 font-weight-medium">
                                        {{ categoryNames[category] || category }}
                                    </h4>
                                </v-col>
                                <v-col
                                    :cols="category === 'OTHER' ? 12 : 12"
                                    :md="category === 'OTHER' ? 4 : 2"
                                    class="text-center"
                                >
                                    <span class="text-caption font-weight-medium">{{
                                        $t('sustainable_room_amenities.price_euro')
                                    }}</span>
                                </v-col>
                                <!-- Only show energy-related headers for non-OTHER categories -->
                                <template v-if="category !== 'OTHER'">
                                    <v-col cols="12" md="2" class="text-center">
                                        <span class="text-caption font-weight-medium">{{
                                            $t('sustainable_room_amenities.type_of_energy')
                                        }}</span>
                                    </v-col>
                                    <v-col cols="12" md="2" class="text-center">
                                        <span class="text-caption font-weight-medium">{{
                                            $t('sustainable_room_amenities.usage_hours_day')
                                        }}</span>
                                    </v-col>
                                    <v-col cols="12" md="2" class="text-center">
                                        <span class="text-caption font-weight-medium">{{
                                            $t('sustainable_room_amenities.energy_consumption_kw')
                                        }}</span>
                                    </v-col>
                                </template>
                            </v-row>
                        </div>

                        <!-- Category Amenities -->
                        <div
                            class="px-2"
                            style="
                                border: 1px solid var(--zsh-primary-dark);
                                border-top: none;
                                border-radius: 0 0 4px 4px;
                            "
                        >
                            <div
                                v-for="amenity in groupedSustainableAmenities[category]"
                                :key="amenity.amenityTypeId || amenity.id"
                                class="py-2"
                                style="border-bottom: 1px solid #e0e0e0"
                            >
                                <v-row dense align="center" no-gutters>
                                    <!-- Adjust column sizes based on category -->
                                    <v-col
                                        :cols="amenity.category === 'OTHER' ? 12 : 12"
                                        :md="amenity.category === 'OTHER' ? 8 : 4"
                                    >
                                        <v-checkbox
                                            v-model="
                                                modelValue.sustainableAmenitiesEnabled[
                                                    amenity.amenityTypeId || amenity.id
                                                ]
                                            "
                                            :label="localized(amenity.name)"
                                            density="compact"
                                            color="primary"
                                            hide-details
                                            :readonly="isFieldReadonly"
                                            :data-cy="`sustainable-amenity-${amenity.amenityTypeId || amenity.id}`"
                                            @update:model-value="
                                                handleAmenityCheckboxChange(
                                                    amenity.amenityTypeId || amenity.id,
                                                    modelValue.sustainableAmenitiesEnabled[
                                                        amenity.amenityTypeId || amenity.id
                                                    ]
                                                )
                                            "
                                        />
                                    </v-col>

                                    <!-- Input fields - only visible when checkbox is checked -->
                                    <template
                                        v-if="
                                            modelValue.sustainableAmenitiesEnabled[amenity.amenityTypeId || amenity.id]
                                        "
                                    >
                                        <v-col
                                            :cols="amenity.category === 'OTHER' ? 12 : 12"
                                            :md="amenity.category === 'OTHER' ? 4 : 2"
                                            class="px-2"
                                        >
                                            <v-text-field
                                                :model-value="
                                                    getAmenityValue(amenity.amenityTypeId || amenity.id, 'price')
                                                "
                                                variant="outlined"
                                                density="compact"
                                                hide-details
                                                class="text-center"
                                                type="number"
                                                step="0.01"
                                                :readonly="isFieldReadonly"
                                                :error="
                                                    hasFieldValidationError(
                                                        amenity.amenityTypeId || amenity.id,
                                                        'price'
                                                    )
                                                "
                                                required
                                                @update:model-value="
                                                    setAmenityValue(
                                                        amenity.amenityTypeId || amenity.id,
                                                        'price',
                                                        $event
                                                    )
                                                "
                                                @blur="markFieldTouched(amenity.amenityTypeId || amenity.id, 'price')"
                                            />
                                        </v-col>

                                        <!-- Only show energy-related fields for non-OTHER categories -->
                                        <template v-if="amenity.category !== 'OTHER'">
                                            <v-col
                                                v-if="
                                                    amenity.energyType ||
                                                    amenity.kwconsumption !== null ||
                                                    getAmenityValue(amenity.amenityTypeId || amenity.id, 'energyType')
                                                "
                                                cols="12"
                                                md="2"
                                                class="px-2"
                                            >
                                                <div class="d-flex align-center justify-center">
                                                    <v-chip
                                                        :key="`energy-${amenity.amenityTypeId || amenity.id}-${getEnergyTypeForAmenity(amenity.amenityTypeId || amenity.id)}`"
                                                        :color="
                                                            getEnergyTypeColor(
                                                                getEnergyTypeForAmenity(
                                                                    amenity.amenityTypeId || amenity.id
                                                                )
                                                            )
                                                        "
                                                        size="small"
                                                        variant="flat"
                                                        :style="{cursor: isFieldReadonly ? 'default' : 'pointer'}"
                                                        :disabled="isFieldReadonly"
                                                        @click="
                                                            handleEnergyTypeClick(amenity.amenityTypeId || amenity.id)
                                                        "
                                                    >
                                                        <span class="font-weight-bold">
                                                            {{
                                                                getEnergyTypeLabel(
                                                                    getEnergyTypeForAmenity(
                                                                        amenity.amenityTypeId || amenity.id
                                                                    )
                                                                )
                                                            }}
                                                        </span>
                                                    </v-chip>
                                                </div>
                                            </v-col>

                                            <v-col v-if="amenity.usageHours !== null" cols="12" md="2" class="px-2">
                                                <v-text-field
                                                    :model-value="
                                                        getAmenityValue(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'usageHours'
                                                        )
                                                    "
                                                    :label="$t('sustainable_room_amenities.usage_hours_day')"
                                                    :placeholder="$t('sustainable_room_amenities.usage_hours_day')"
                                                    variant="outlined"
                                                    density="compact"
                                                    :readonly="isFieldReadonly"
                                                    class="text-center"
                                                    type="number"
                                                    step="0.01"
                                                    :error="
                                                        hasFieldValidationError(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'usageHours'
                                                        )
                                                    "
                                                    hide-details
                                                    required
                                                    @update:model-value="
                                                        setAmenityValue(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'usageHours',
                                                            $event
                                                        )
                                                    "
                                                    @blur="
                                                        markFieldTouched(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'usageHours'
                                                        )
                                                    "
                                                />
                                            </v-col>

                                            <v-col v-if="amenity.kwconsumption !== null" cols="12" md="2" class="px-2">
                                                <v-text-field
                                                    :model-value="
                                                        getAmenityValue(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'kwconsumption'
                                                        )
                                                    "
                                                    variant="outlined"
                                                    density="compact"
                                                    class="text-center"
                                                    type="number"
                                                    step="0.01"
                                                    :readonly="isFieldReadonly"
                                                    :error="
                                                        hasFieldValidationError(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'kwconsumption'
                                                        )
                                                    "
                                                    hide-details
                                                    required
                                                    @update:model-value="
                                                        setAmenityKwConsumption(
                                                            amenity.amenityTypeId || amenity.id,
                                                            $event
                                                        )
                                                    "
                                                    @blur="
                                                        markFieldTouched(
                                                            amenity.amenityTypeId || amenity.id,
                                                            'kwconsumption'
                                                        )
                                                    "
                                                />
                                            </v-col>
                                        </template>

                                        <!-- Empty columns for OTHER category to maintain alignment -->
                                        <template v-else>
                                            <!-- No additional columns needed for OTHER category since headers are hidden -->
                                        </template>
                                    </template>

                                    <!-- Empty columns when checkbox is not checked -->
                                    <template v-else>
                                        <v-col
                                            :cols="amenity.category === 'OTHER' ? 12 : 12"
                                            :md="amenity.category === 'OTHER' ? 4 : 2"
                                        />
                                        <!-- Only show empty energy-related columns for non-OTHER categories -->
                                        <template v-if="amenity.category !== 'OTHER'">
                                            <v-col cols="12" md="2" />
                                            <v-col cols="12" md="2" />
                                            <v-col cols="12" md="2" />
                                        </template>
                                    </template>
                                </v-row>

                                <!-- Validation errors display - shown as separate line within amenity row -->
                                <div
                                    v-if="
                                        modelValue.sustainableAmenitiesEnabled[amenity.amenityTypeId || amenity.id] &&
                                        hasAmenityValidationErrors(amenity.amenityTypeId || amenity.id)
                                    "
                                    class="pl-1 pr-2 pt-3 pb-1"
                                >
                                    <v-alert type="error" variant="tonal" density="compact" class="mb-0" border="start">
                                        <template v-slot:prepend>
                                            <v-icon size="small">mdi-alert-circle</v-icon>
                                        </template>
                                        <div class="text-caption">
                                            <div
                                                v-for="error in getValidationErrors(
                                                    amenity.amenityTypeId || amenity.id
                                                )"
                                                :key="error.field"
                                                class="d-flex align-center mb-1"
                                            >
                                                <v-icon size="x-small" class="mr-1">mdi-circle-small</v-icon>
                                                {{ error.message }}
                                            </div>
                                        </div>
                                    </v-alert>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <!-- Energy Type Legend -->
                <v-row dense class="mt-4">
                    <v-col cols="12">
                        <div class="d-flex align-center justify-center">
                            <span class="mr-4">{{ $t('sustainable_room_amenities.energy_legend_title') }}</span>
                            <v-chip color="blue" size="small" variant="flat" class="mr-2">
                                <span class="font-weight-bold">E</span>
                            </v-chip>
                            <span class="mr-4">{{ $t('sustainable_room_amenities.energy_electrical') }}</span>
                            <v-chip color="purple" size="small" variant="flat" class="mr-2">
                                <span class="font-weight-bold">G</span>
                            </v-chip>
                            <span class="mr-4">{{ $t('sustainable_room_amenities.energy_gas') }}</span>
                            <v-chip color="red" size="small" variant="flat" class="mr-2">
                                <span class="font-weight-bold">O</span>
                            </v-chip>
                            <span>{{ $t('sustainable_room_amenities.energy_oil') }}</span>
                        </div>
                    </v-col>
                </v-row>
            </template>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import {nextTick} from 'vue'

    const {t} = useI18n()

    const props = defineProps<{
        modelValue: any
        sustainableAmenitiesData: any[]
        sustainableAmenitiesLoading: boolean
        sustainableAmenityCategories: string[]
        groupedSustainableAmenities: Record<string, any[]>
        categoryNames: Record<string, string>
        isFieldReadonly: boolean
        getAmenityValue: (amenityId: string | number, field: string) => any
        setAmenityValue: (amenityId: string | number, field: string, value: any) => void
        setAmenityKwConsumption: (amenityId: string | number, value: any) => void
        cycleEnergyType: (amenityId: string | number) => void
        localized: (obj: any) => string
        getAmenityTypeConstraints: (amenityTypeId: string | number) => any
        validateAmenityInput: (amenityTypeId: string | number, field: string, value: any) => boolean
        getAmenityValidationMessage: (amenityTypeId: string | number, field: string) => string
    }>()

    // Track touched state for each field
    const touchedFields = ref<Record<string, Record<string, boolean>>>({})

    // Helper function to mark field as touched
    const markFieldTouched = (amenityId: string | number, field: string) => {
        if (!touchedFields.value[amenityId]) {
            touchedFields.value[amenityId] = {}
        }
        touchedFields.value[amenityId][field] = true
    }

    // Helper function to check if field is touched
    const isFieldTouched = (amenityId: string | number, field: string) => {
        return touchedFields.value[amenityId]?.[field] || false
    }

    // Helper function to check if specific field has validation error
    const hasFieldValidationError = (amenityId: string | number, field: string) => {
        const amenity = props.sustainableAmenitiesData.find(a => (a.amenityTypeId || a.id) === amenityId)
        if (!amenity) return false

        const isFieldTouchedValue = isFieldTouched(amenityId, field)

        // Check if field is invalid
        let isInvalid = false
        if (field === 'price') {
            const price = props.getAmenityValue(amenityId, 'price')
            isInvalid = !price || price <= 0
        } else if (field === 'kwconsumption' && amenity.kwconsumption !== null) {
            const kwValue = props.getAmenityValue(amenityId, 'kwconsumption')
            isInvalid =
                kwValue === null ||
                kwValue === undefined ||
                kwValue === '' ||
                !props.validateAmenityInput(amenityId, 'kwconsumption', kwValue)
        } else if (field === 'usageHours' && amenity.usageHours !== null) {
            const usageValue = props.getAmenityValue(amenityId, 'usageHours')
            isInvalid =
                usageValue === null ||
                usageValue === undefined ||
                usageValue === '' ||
                !props.validateAmenityInput(amenityId, 'usageHours', usageValue)
        }

        // Show error if field is invalid (regardless of touched state for immediate feedback)
        return isInvalid
    }

    // Helper function to check if amenity has validation errors
    const hasAmenityValidationErrors = (amenityId: string | number) => {
        const amenity = props.sustainableAmenitiesData.find(a => (a.amenityTypeId || a.id) === amenityId)
        if (!amenity) return false

        // Check price validation (show if touched OR invalid)
        const price = props.getAmenityValue(amenityId, 'price')
        const isPriceTouched = isFieldTouched(amenityId, 'price')
        const isPriceInvalid = !price || price <= 0
        if (isPriceTouched || isPriceInvalid) {
            if (isPriceInvalid) return true
        }

        // Skip energy-related validation for OTHER category amenities
        if (amenity.category === 'OTHER') {
            return false
        }

        // Check kW consumption validation if field exists (show if touched OR invalid)
        if (amenity.kwconsumption !== null) {
            const kwValue = props.getAmenityValue(amenityId, 'kwconsumption')
            const isKwTouched = isFieldTouched(amenityId, 'kwconsumption')
            const isKwInvalid =
                kwValue === null ||
                kwValue === undefined ||
                kwValue === '' ||
                !props.validateAmenityInput(amenityId, 'kwconsumption', kwValue)
            if (isKwTouched || isKwInvalid) {
                if (isKwInvalid) return true
            }
        }

        // Check usage hours validation if field exists (show if touched OR invalid)
        if (amenity.usageHours !== null) {
            const usageValue = props.getAmenityValue(amenityId, 'usageHours')
            const isUsageTouched = isFieldTouched(amenityId, 'usageHours')
            const isUsageInvalid =
                usageValue === null ||
                usageValue === undefined ||
                usageValue === '' ||
                !props.validateAmenityInput(amenityId, 'usageHours', usageValue)
            if (isUsageTouched || isUsageInvalid) {
                if (isUsageInvalid) return true
            }
        }

        return false
    }

    // Helper function to get validation errors for display
    const getValidationErrors = (amenityId: string | number) => {
        const amenity = props.sustainableAmenitiesData.find(a => (a.amenityTypeId || a.id) === amenityId)
        if (!amenity) return []

        const errors = []

        // Check price validation (show if touched OR invalid)
        const price = props.getAmenityValue(amenityId, 'price')
        const isPriceTouched = isFieldTouched(amenityId, 'price')
        const isPriceInvalid = !price || price <= 0
        if ((isPriceTouched || isPriceInvalid) && isPriceInvalid) {
            errors.push({field: 'price', message: t('sustainable_room_amenities.price_required_greater_than_zero')})
        }

        // Skip energy-related validation for OTHER category amenities
        if (amenity.category === 'OTHER') {
            return errors
        }

        // Check kW consumption validation if field exists (show if touched OR invalid)
        if (amenity.kwconsumption !== null) {
            const kwValue = props.getAmenityValue(amenityId, 'kwconsumption')
            const isKwTouched = isFieldTouched(amenityId, 'kwconsumption')
            const isKwInvalid =
                kwValue === null ||
                kwValue === undefined ||
                kwValue === '' ||
                !props.validateAmenityInput(amenityId, 'kwconsumption', kwValue)
            if ((isKwTouched || isKwInvalid) && isKwInvalid) {
                const message =
                    kwValue !== null && kwValue !== undefined && kwValue !== ''
                        ? props.getAmenityValidationMessage(amenityId, 'kwconsumption')
                        : t('sustainable_room_amenities.kw_consumption_required')
                errors.push({field: 'kwconsumption', message})
            }
        }

        // Check usage hours validation if field exists (show if touched OR invalid)
        if (amenity.usageHours !== null) {
            const usageValue = props.getAmenityValue(amenityId, 'usageHours')
            const isUsageTouched = isFieldTouched(amenityId, 'usageHours')
            const isUsageInvalid =
                usageValue === null ||
                usageValue === undefined ||
                usageValue === '' ||
                !props.validateAmenityInput(amenityId, 'usageHours', usageValue)
            if ((isUsageTouched || isUsageInvalid) && isUsageInvalid) {
                const message =
                    usageValue !== null && usageValue !== undefined && usageValue !== ''
                        ? props.getAmenityValidationMessage(amenityId, 'usageHours')
                        : t('sustainable_room_amenities.usage_hours_required')
                errors.push({field: 'usageHours', message})
            }
        }

        return errors
    }

    // Helper function to mark all input fields of other checked amenities as touched
    const markOtherCheckedAmenitiesAsTouched = (excludeAmenityId?: string | number) => {
        if (!props.modelValue.sustainableAmenitiesEnabled) {
            return
        }

        // Get all currently checked amenities, excluding the one we just clicked
        const checkedAmenities = Object.keys(props.modelValue.sustainableAmenitiesEnabled)
            .filter(amenityId => props.modelValue.sustainableAmenitiesEnabled[amenityId])
            .filter(amenityId => amenityId != excludeAmenityId) // Exclude the amenity we just checked

        // Mark all input fields of checked amenities as touched
        checkedAmenities.forEach(amenityId => {
            const amenity = props.sustainableAmenitiesData.find(a => {
                const matchesId = a.id == amenityId
                const matchesTypeId = a.amenityTypeId == amenityId
                return matchesId || matchesTypeId
            })

            if (!amenity) {
                return
            }

            // Mark price field as touched
            markFieldTouched(amenityId, 'price')

            // Skip energy-related fields for OTHER category amenities
            if (amenity.category === 'OTHER') {
                return
            }

            // Mark kW consumption field as touched if it exists
            if (amenity.kwconsumption !== null) {
                markFieldTouched(amenityId, 'kwconsumption')
            }

            // Mark usage hours field as touched if it exists
            if (amenity.usageHours !== null) {
                markFieldTouched(amenityId, 'usageHours')
            }
        })
    }

    // Watch for changes in sustainable amenities enabled state
    watch(
        () => props.modelValue.sustainableAmenitiesEnabled,
        (newValue, oldValue) => {
            if (!newValue) {
                return
            }

            // Handle initial case where oldValue might be undefined
            const safeOldValue = oldValue || {}

            // Check if any amenity was just checked (changed from false/undefined to true)
            const wasAmenityJustChecked = Object.keys(newValue).some(amenityId => {
                const isNowChecked = newValue[amenityId] === true
                const wasPreviouslyChecked = safeOldValue[amenityId] === true
                return isNowChecked && !wasPreviouslyChecked
            })

            // If an amenity was just checked, mark other checked amenities' fields as touched
            // Also trigger if we have any checked amenities (for existing rooms)
            const hasCheckedAmenities = Object.keys(newValue).some(amenityId => newValue[amenityId] === true)

            if (wasAmenityJustChecked || (hasCheckedAmenities && Object.keys(safeOldValue).length === 0)) {
                nextTick(() => {
                    markOtherCheckedAmenitiesAsTouched()
                })
            }
        },
        {deep: true, immediate: false}
    )

    // Reactive helper functions for energy type
    const getEnergyTypeForAmenity = (amenityId: string | number) => {
        return props.getAmenityValue(amenityId, 'energyType')
    }

    const getEnergyTypeColor = (energyType: string) => {
        switch (energyType) {
            case 'ELECTRICAL':
                return 'blue'
            case 'GAS':
                return 'purple'
            case 'OIL':
                return 'red'
            default:
                return 'blue'
        }
    }

    const getEnergyTypeLabel = (energyType: string) => {
        switch (energyType) {
            case 'ELECTRICAL':
                return 'E'
            case 'GAS':
                return 'G'
            case 'OIL':
                return 'O'
            default:
                return 'E'
        }
    }

    // Handle energy type click
    const handleEnergyTypeClick = (amenityId: string | number) => {
        if (!props.isFieldReadonly) {
            props.cycleEnergyType(amenityId)
        }
    }

    // Handle amenity checkbox change
    const handleAmenityCheckboxChange = (amenityId: string | number, isChecked: boolean) => {
        // If checkbox was just checked, mark other checked amenities' fields as touched
        if (isChecked) {
            nextTick(() => {
                markOtherCheckedAmenitiesAsTouched(amenityId)
            })
        }
    }

    const emit = defineEmits<{
        'update:modelValue': [value: any]
    }>()
</script>
